import React from 'react'
import type { IUserObject } from 'types'
import type { NewUserParams } from 'helpers/api'

/** Stable interface — do NOT use ReturnType<typeof _useAuthProvider> */
export interface AuthStore {
  user: IUserObject | undefined
  onPaidPlan: boolean
  isTeamManager: boolean

  login: (emailAddress: string, password: string) => Promise<void>
  register: (userData: NewUserParams) => Promise<void>
  logout: (serverSideLogout?: boolean) => Promise<void>
  updateUser: (info: Partial<IUserObject>) => Promise<void>
  startGoogleAuth: (template?: string) => Promise<void>
  startMicrosoftAuth: (template?: string) => Promise<void>
  startSSOAuth: (email: string) => Promise<void>
  startMasquerade: (token: string) => Promise<void>
  refreshUser: () => Promise<void>
}

/** Context generic stays EXACTLY the same everywhere */
type Ctx = AuthStore | undefined

/** Cache the Context on globalThis so HMR reuses the same object */
const KEY = '__APP__AuthContext'
const existing = (globalThis as any)[KEY] as React.Context<Ctx> | undefined

export const AuthContext: React.Context<Ctx> = existing ?? React.createContext<Ctx>(undefined)

if (!(globalThis as any)[KEY]) {
  ;(globalThis as any)[KEY] = AuthContext
}

/** Consumer hook */
export const useAuth = (): AuthStore => {
  const ctx = React.useContext(AuthContext)
  if (!ctx) throw new Error('useAuth can only be used within an Auth context')
  return ctx
}

/** If this file changes, do a full reload to avoid stale consumer/provider splits */
if (import.meta.hot) {
  import.meta.hot.accept(() => import.meta.hot.invalidate())
}
